import type {
  CustomerSupportSkeletonQueryLimits,
  OrderStatusSkeletonQueryLimits,
  SectionSkeletonQueryLimits,
  VoucherSkeletonQueryLimits
} from './skeletons';
import type { DataLayerQueryLimits, Device } from './types';
import type { AddonIconNode, ProductAttributes } from './transformers/types';
import type { ThemeProps } from '@pixum/theme-provider';
import { MagazineFocus } from '@pixum/magazine';
import { AddonIconType, type AddonIconTypeValue } from '@pixum/card';
import type { LocaleIso } from 'utils';

export const DEVICES = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
  DECO: 'deco',
  BOTH: 'both'
} as const;

/**
 * Maps OS names to PSI service OS codes.
 * Based on Gatsby implementation: packages/core/src/constants/operatingSystem.ts
 */
export const OS_MAP: Record<string, string> = {
  Windows: 'win',
  macOS: 'mac',
  'Mac OS': 'mac',
  Linux: 'linux',
  Ubuntu: 'linux'
};

// find a name better name to separate from the Device type
export type DeviceType = (typeof DEVICES)[keyof typeof DEVICES];

export const SCREEN_SIZES = {
  phoneXXS: 0,
  phoneXS: 1,
  phoneS: 2,
  desktop: 3
} as const;

export const SEASON = {
  SPRING: 'spring',
  SUMMER: 'summer',
  FALL: 'fall',
  WINTER: 'winter',
  CHRISTMAS: 'christmas'
} as const;

export const WINTER_DATE_RANGE = {
  start: new Date(`${new Date().getFullYear()}-10-21`),
  end: new Date(`${new Date().getFullYear() + 1}-03-31`)
};

export const SUMMER_DATE_RANGE = {
  start: new Date(`${new Date().getFullYear()}-04-01`),
  end: new Date(`${new Date().getFullYear() + 1}-10-20`)
};

export const SECTION_DEFAULT_QUERY_LIMITS: SectionSkeletonQueryLimits = {
  mediaLimit: 10,
  linksLimit: 10,
  sectionsLimit: 15,
  constantsLimit: 10
};

export const VOUCHER_DEFAULT_QUERY_LIMITS: VoucherSkeletonQueryLimits = {
  vouchersLimit: 5,
  badgesLimit: 5
};

export const GROUP_DEFAULT_QUERY_LIMITS = {
  sectionLimit: 10,
  sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
  productCategoryPageLimits: {
    mainLimit: 10,
    keyVisualLimits: SECTION_DEFAULT_QUERY_LIMITS,
    mediaLimit: 10,
    productSpecificationsLimit: 10
  }
};

export const TABLE_DEFAULT_QUERY_LIMITS = {
  attributeLimit: 10,
  columnsLimit: 8,
  rowsLimit: 10
};

export const MASTER_PAGE_DEFAULT_QUERY_LIMITS = {
  keyVisualsLimit: 5,
  contentLimit: 30,
  constantsLimit: 140,
  articleSectionsLimit: {
    sectionLimit: 10,
    sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
    groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
    tableLimits: TABLE_DEFAULT_QUERY_LIMITS
  },
  keyVisualLimits: {
    ctaLimit: 5,
    mediaLimit: 10,
    footnotesLimit: 5
  },
  mainAwardLimits: SECTION_DEFAULT_QUERY_LIMITS,
  vouchersLimits: VOUCHER_DEFAULT_QUERY_LIMITS,
  generalLinkLimit: 20,
  expressDesignerLimits: {
    productsLimit: 10
  }
};

export const CONTACT_DEFAULT_LIMITS: CustomerSupportSkeletonQueryLimits = {
  constantsLimit: 60,
  mediaLimit: 2,
  prefilledRequestTopicsLimit: 16,
  topicsLimit: 16
};

/**
 * Asset CDN host for static assets (icons, images)
 * Uses absolute HTTPS URL (not protocol-relative) for better compatibility with:
 * - Schema.org structured data (requires absolute URLs)
 * - PWA manifest icons
 * - HTML meta tags
 */
export const ASSET_HOST = 'https://assets.pixum.com';

/**
 * Icon version for cache busting
 * Increment this when icons change to force browsers to reload (e.g., '1' -> '2')
 * Used in: SeoTransformer, manifest.ts, ORGANIZATION_DATA
 * Note: Version parameter will be used as ?v={ICON_VERSION}
 */
export const ICON_VERSION = '3';

export const ORGANIZATION_DATA = {
  type: 'website',
  name: 'Pixum',
  url: 'https://www.pixum.de/',
  logo: `${ASSET_HOST}/assets/icons/favicon-192x192-v${ICON_VERSION}.png?v=${ICON_VERSION}`,
  sameAs: [
    'https://www.pinterest.de/pixum/',
    'https://www.youtube.com/pixum',
    'https://www.facebook.com/pixum',
    'https://blog.pixum.de/',
    'https://www.instagram.com/pixum/',
    'https://twitter.com/pixum_de',
    'https://de.wikipedia.org/wiki/Pixum',
    'https://de.trustpilot.com/review/www.pixum.de',
    'https://www.trustedshops.de/bewertung/info_XFAB7C17C298F48909E8C9F2BFAA5794B.html'
  ]
};

export const ORDER_STATUS_DEFAULT_LIMITS: OrderStatusSkeletonQueryLimits = {
  constantsLimit: 20,
  summariesLimit: 14,
  navigationLimits: {
    limit: 10,
    childrenLimit: 1 // Can not be set to 0
  },
  orderSummaryLimits: {
    mediaLimit: 10
  }
};

export const DATA_LAYER_DEFAULT_QUERY_LIMITS: DataLayerQueryLimits = {
  pageLimits: {
    slugs: 1000,
    cartPage: {
      constantsLimit: 200
    },
    customerSupportLimits: {
      ...CONTACT_DEFAULT_LIMITS
    },
    footerLimits: {
      aboutLimit: 10,
      appLinksLimit: 3,
      contactLimits: CONTACT_DEFAULT_LIMITS,
      constantsLimit: 30,
      downloadSoftwareLinksLimit: 2,
      infoLinksLimit: 16,
      inspirationLinksLimit: 16,
      notesLinksLimit: 10,
      paymentLinksLimit: 10,
      secureLinksLimit: 3,
      serviceLinksLimit: 16,
      shippingLinksLimit: 4,
      socialLinksLimit: 5,
      contentLimit: 30,
      articleLimits: MASTER_PAGE_DEFAULT_QUERY_LIMITS.articleSectionsLimit
    },
    headerLimits: {
      menuItemsLimit: 10,
      constantsLimit: 12,
      authLimits: {
        authConstantsLimit: 23,
        loginConstantsLimit: 12,
        passwordConstantsLimit: 11,
        registerConstantsLimit: 26,
        membershipAdvantagesConstantsLimit: 3,
        newsletterGiftConstantsLimit: 6
      },
      navigationLimits: {
        limit: 16,
        childrenLimit: 16
      }
    },
    landingPage: {
      mainLimit: 12,
      mainLimits: {
        sectionLimit: 10,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      articleSectionsLimit: {
        sectionLimit: 5,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: SECTION_DEFAULT_QUERY_LIMITS
      },
      keyVisualLimits: SECTION_DEFAULT_QUERY_LIMITS,
      mainAwardLimits: SECTION_DEFAULT_QUERY_LIMITS,
      voucherLimits: VOUCHER_DEFAULT_QUERY_LIMITS,
      awardsLimits: {
        sectionLimit: 7,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      mediaLimit: 10
    },
    productCategoryPage: {
      mainLimit: 12,
      articleSectionsLimit: {
        sectionLimit: 5,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      keyVisualLimits: {
        mediaLimit: 10,
        linksLimit: 10,
        sectionsLimit: 2,
        constantsLimit: 10
      },
      mediaLimit: 10,
      productSpecificationsLimit: 10
    },
    shopgroupPage: {
      mainLimit: 20,
      articleSectionsLimit: {
        sectionLimit: 5,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      keyVisualsLimit: 2,
      keyVisualLimits: SECTION_DEFAULT_QUERY_LIMITS,
      mainLimits: {
        sectionLimit: 13,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      mainAwardLimits: SECTION_DEFAULT_QUERY_LIMITS,
      awardsLimits: {
        sectionLimit: 6,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      mediaLimit: 10
    },
    startPage: {
      mainLimit: 15,
      articleSectionsLimit: {
        sectionLimit: 10,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      keyVisualLimits: SECTION_DEFAULT_QUERY_LIMITS,
      mainAwardLimits: SECTION_DEFAULT_QUERY_LIMITS,
      awardsLimits: {
        sectionLimit: 6,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS
      },
      orderStatusLimits: ORDER_STATUS_DEFAULT_LIMITS
    },
    servicePage: {
      mainLimit: 15,
      mediaLimit: 2,
      articleLimits: {
        sectionLimit: 15,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      keyVisualsLimit: 2,
      keyVisualLimits: SECTION_DEFAULT_QUERY_LIMITS,
      mainAwardLimits: SECTION_DEFAULT_QUERY_LIMITS,
      awardsLimits: {
        sectionLimit: 6,
        sectionLimits: SECTION_DEFAULT_QUERY_LIMITS,
        groupLimits: GROUP_DEFAULT_QUERY_LIMITS,
        tableLimits: TABLE_DEFAULT_QUERY_LIMITS
      },
      orderStatusLimits: ORDER_STATUS_DEFAULT_LIMITS
    },
    mediaLimit: 10
  }
};

export const CONTENTFUL_MOBILE_PRESENTATION_TYPES = {
  SWIPEABLE: 'swipeable',
  SINGLE_COLUMN_GRID: 'single-column-grid',
  TWO_COLUMN_GRID: 'two-column-grid'
} as const;

export const CONTENTFUL_CARD_TYPES = {
  PRODUCT: 'ProductCard',
  ORDER_WAYS: 'OrderWays',
  ORDER_WAYS_DIALOG: 'OrderWaysDialog',
  NAVIGATION: 'NavigationCard',
  TAGS_COLLECTION: 'TagsCollection'
} as const;

export const CONTENTFUL_ARTICLE_TYPES = {
  MagazineFocusText: 'MagazineFocusText',
  MagazineFocusImage: 'MagazineFocusImage',
  AnchorLinks: 'AnchorLinks',
  ContentNarrow: 'ContentNarrow',
  InfoActionBanner: 'InfoActionBanner',
  FAQ: 'FAQ',
  Editorial: 'Editorial',
  Awards: 'Awards',
  TagsCollection: CONTENTFUL_CARD_TYPES.TAGS_COLLECTION,
  USP: 'USP',
  ProductCard: CONTENTFUL_CARD_TYPES.PRODUCT,
  OrderWays: CONTENTFUL_CARD_TYPES.ORDER_WAYS,
  OrderWaysDialog: CONTENTFUL_CARD_TYPES.ORDER_WAYS_DIALOG,
  NavigationCard: CONTENTFUL_CARD_TYPES.NAVIGATION,
  Table: 'Table',
  AuthorBox: 'AuthorBox',
  Newsletter: 'Newsletter'
} as const;

export const CONTENTFUL_GROUPED_SECTION_TYPES = {
  CARD_WITH_CHIPS: 'card-with-chips',
  MAGAZINE_WITH_CHIPS: 'magazine-with-chips',
  MAGAZINE_WITH_ACCORDION: 'magazine-with-accordion'
} as const;

export const CONFIGURATOR_IDENTIFIER = 'configurator';
export const EDITOR_IDENTIFIER = 'editor';

export const THEME_DEFAULT_PROPS: ThemeProps = {
  base: 'ink',
  accent: 'brand',
  brightness: 'bright'
};

export const THEME_DEFAULT_CLASSES = ['theme__base-ink', 'theme__accent-brand', 'theme__brightness-bright'].join(' ');

export const ACCESSIBILITY_ROLE_REGION = 'region';

export const CONTENTFUL_PAGE_STATUS_TYPES = {
  TESTING: 'Testing',
  LIVE: 'Live',
  INACTIVE: 'In-active'
} as const;

export const PRODUCT_ATTRIBUTE_STATUS_VALUES = {
  ACTIVE: 'active',
  TESTING: 'testing',
  INACTIVE: 'in-active'
};

export const PRODUCT_ATTRIBUTE_NAMES_VALUES = {
  STATUS: 'status',
  PRICE: 'euro',
  EDITOR_ROTATE_ALLOWED: 'editor_rotate_allowed'
};

// Internal name (idOnlyForCms) of the price list link side-loaded for the express designer price hint
export const PRICE_INFO_LINK_ID = 'link_mix_pricelist';

export const EXPRESS_DESIGNER_PRODUCT_ATTRIBUTES: ProductAttributes = {
  status: PRODUCT_ATTRIBUTE_NAMES_VALUES.STATUS,
  price: PRODUCT_ATTRIBUTE_NAMES_VALUES.PRICE,
  editorRotateAllowed: PRODUCT_ATTRIBUTE_NAMES_VALUES.EDITOR_ROTATE_ALLOWED
};

export const MAGAZINE_VARIANTS = {
  CONTENT_RIGHT: 'content-right',
  CONTENT_LEFT: 'content-left'
} as const;

export const DEFAULT_BRAND_BAR_ID = 'brand_bar';

/**
 * Layout type constants used for header and footer variants.
 * These values are used in LayoutConfiguration content type in Contentful
 * to control which header/footer variant should be rendered.
 *
 * - Default: Full header with navigation and full footer
 * - Slim: Minimal header (user icon only) and slim footer
 * - SeaSlim: Sea-themed slim variant
 * - Cart: Special layout for cart page
 */
export const LayoutTypes = {
  Default: 'default',
  SeaSlim: 'sea-slim',
  Slim: 'slim',
  Cart: 'cart'
} as const;

export const PRIVACY_POLICE_CONTENTFUL_SYS_ID = '2XyesvmY70qhWgPlkPubg7';
export const AUTHENTICITY_REVIEWS_LINK_CONTENTFUL_SYS_ID = '2dXikpzI3FkMnwh2f3AzbC';

export const AddonIconTypes = {
  AddonFlag: 'AddonFlag',
  RemoveIcon: 'RemoveIcon',
  ZoomIcon: 'ZoomIcon',
  CheckIcon: 'CheckIcon',
  EventFlag: 'EventFlag',
  ImageFlag: 'ImageFlag'
} as const;

export const ADDON_ICON_TYPE_MAP: Record<AddonIconNode, AddonIconTypeValue> = {
  [AddonIconTypes.AddonFlag]: AddonIconType.AddonFlag,
  [AddonIconTypes.RemoveIcon]: AddonIconType.RemoveIcon,
  [AddonIconTypes.ZoomIcon]: AddonIconType.ZoomIcon,
  [AddonIconTypes.CheckIcon]: AddonIconType.CheckIcon,
  [AddonIconTypes.EventFlag]: AddonIconType.EventFlag,
  [AddonIconTypes.ImageFlag]: AddonIconType.ImageFlag
} as const;

export const VOUCHER_TYPES = {
  PERCENTAGE: 'percentage',
  TOTAL: 'total',
  FREE_AMOUNT: 'free_amount',
  FREE_SHIPPING: 'free_shipping'
} as const;

export const VOUCHER_NEW_VALUE_TYPES = {
  EXACT_AMOUNT: 'Exact amount',
  PERCENTAGE: 'Percentage',
  OTHER: 'Other'
} as const;

/**
 * CDN host for static assets (images, videos, etc.)
 * Used by transformers to construct asset URLs
 */
export const CDN_HOST = 'https://cdn.pixum.com';

/** Imgix source for static assets (SVG input is enabled here, unlike the CMS source). */
export const IMGIX_ASSETS_HOST = 'https://pixum-assets.imgix.net';

/** Imgix source for Contentful images; protocol-relative because it replaces the protocol-relative Contentful host. */
export const IMGIX_CMS_HOST = '//pixum-cms.imgix.net';

export const CUSTOM_JAVASCRIPT_TYPE = {
  MODULE: 'module',
  CLASSIC: 'classic'
} as const;

export const KeyVisualVariantTypes = {
  HighContrastWithAwards: 'High-Contrast with Awards',
  HighContrast: 'High-Contrast',
  Standard: 'standard',
  Blog: 'Blog',
  MagazineKeyVisual: 'MagazineKeyVisual',
  NewsletterMagazineKeyVisual: 'NewsletterMagazineKeyVisual',
  Download: 'Download'
} as const;

export const KeyVisualEmphasisTypes = {
  High: 'high',
  Blog: 'blog',
  Standard: 'standard'
} as const;

export const MAGAZINE_FOCUS = {
  IMAGE: MagazineFocus.IMAGE,
  TEXT: MagazineFocus.TEXT
} as const;

export const CONTENTFUL_MAGAZINE_TYPES = {
  IMAGE: 'MagazineFocusImage',
  TEXT: 'MagazineFocusText'
} as const;

export const PRODUCT_NAVIGATION_IDS = [
  'navi_fab',
  'navi_fge',
  'navi_pfb',
  'navi_pfk',
  'navi_fgk',
  'navi_pwb',
  'navi_phh'
];

/**
 * Locale fallback mapping for Contentful queries.
 * When a page is not found in the requested locale, the system will attempt
 * to fetch it from the fallback locale defined here.
 *
 * This is necessary because Contentful's built-in locale fallback only works
 * for field values, not for WHERE clause filtering in GraphQL queries.
 *
 * Fallback chain examples:
 * - de-DE → de → en
 * - fr-FR → fr → en
 * - en-IE → en-GB → en
 */
export const LOCALE_FALLBACK_MAP: Record<string, string> = {
  // Bulgarian locales
  'bg-BG': 'bg',
  bg: 'en-GB',
  // Czech locales
  'cs-CZ': 'cs',
  cs: 'en-GB',
  // Danish locales
  'da-DK': 'en',
  // German locales
  'de-DE': 'de',
  'de-AT': 'de',
  'de-CH': 'de',
  'de-LU': 'de',
  de: 'en',
  // English locales
  'en-IE': 'en-GB',
  'en-GB': 'en',
  // Spanish locales
  'es-ES': 'en',
  // Finnish locales
  'fi-FI': 'fi',
  fi: 'en',
  // French locales
  'fr-FR': 'fr',
  'fr-BE': 'fr',
  'fr-CH': 'fr',
  fr: 'en',
  // Hungarian locales
  'hu-HU': 'hu',
  hu: 'en-GB',
  // Italian locales
  'it-IT': 'it',
  'it-CH': 'it',
  it: 'en',
  // Dutch locales
  'nl-NL': 'nl',
  'nl-BE': 'nl',
  nl: 'en',
  // Polish locales
  'pl-PL': 'pl',
  pl: 'en-GB',
  // Portuguese locales
  'pt-PT': 'en-GB',
  // Swedish locales
  'sv-SE': 'en'
} as const;

export const PRICE_CONSTANT_ID = 'i18n_price';

/**
 * Platform types used in Page content model.
 * Indicates which platform a page is intended for.
 */
export const PLATFORM_TYPES = {
  NEXTJS: 'next.js',
  GATSBY: 'gatsby'
} as const;

export const REVIEWS_LOCALE_FALLBACK_MAP: Record<string, LocaleIso> = {
  'pl-PL': 'en-GB',
  'hu-HU': 'en-GB',
  'bg-BG': 'en-GB',
  'cs-CZ': 'en-GB'
};

/**
 * Page type constants from Contentful.
 * These values match the __typename returned by Contentful GraphQL API.
 */
/**
 * MasterPage type values from Contentful
 * These map to different rendering behaviors within the MasterPage content type
 */
export const MASTER_PAGE_TYPES = {
  EDITORIAL: 'editorial',
  DOWNLOAD: 'download',
  LANDING: 'landing',
  SHOPGROUP: 'shopgroup',
  STARTPAGE: 'startpage',
  SELECTCOUNTRY: 'selectcountry',
  SERVICE_BOT: 'service-bot'
} as const;

export const PAGE_TYPES = {
  CartPage: 'PagePageCart',
  LandingPage: 'PagePageLandingpage',
  MasterPage: 'MasterPage',
  ProductCategoryPage: 'PageProductCategory',
  ShopgroupPage: 'PageShopgroup',
  StartPage: 'PagePageStart',
  ServicePage: 'PageService',
  DownloadPage: 'DownloadPage',
  SelectCountryPage: 'SelectCountry'
} as const;

export const ACTIVE_VOUCHER_STATUS = {
  ACTIVE: 'active',
  ERROR: 'error',
  DEFAULT: 'default'
} as const;

/**
 * internalName values of ModelCustomerSupportTopic entries that trigger
 * cancellation-specific form behaviour (slot reveal, button label change, message composition).
 * Populated in coordination with the content team before release.
 */
export const CANCELLATION_TOPIC_KEYS: string[] = ['model_customerSupport_topic_cancel_contract'];

export const CANCEL_ORDER_LINK_ID = 'link_footer_service_cancel-order';

/**
 * internalName values of Contentful service page entries for which the customer
 * support contact form should open automatically with the cancellation topic pre-selected.
 */
export const AUTO_OPEN_CONTACT_FORM_PAGE_NAMES = ['page_service_cancel-order'] as const;

export const LINK_TYPES = {
  ADD_TO_CART: 'add-to-cart',
  COOKIE_SETTINGS: 'cookie-settings',
  DIRECT_SOFTWARE_DOWNLOAD: 'direct-download-software',
  DOWNLOAD_APP: 'download-app',
  DOWNLOAD_APP_DEEPLINK: 'download-app-deeplink',
  DOWNLOAD_SOFTWARE: 'download-software',
  DOWNLOAD_SOFTWARE_LINK: 'download-software-link',
  EMPTY_CART: 'empty-cart',
  EMPTY_CART_LINK: 'empty-cart-cta',
  ONLINE_DESIGNER: 'online-designer',
  ORDER_WAYS: 'order-ways',
  ORDER_WAYS_PRODUCT: 'order-ways-product',
  PHOTOBOOK_SOFTWARE: 'photobook-software',
  PRIVACY: 'privacy',
  SEE_MORE: 'see-more',
  SELECT_COUNTRY: 'select-country',
  SERVICE: 'service'
} as const;

export const PRODUCT_ALIGNMENT = {
  LANDSCAPE: 'landscape',
  PORTRAIT: 'portrait',
  SQUARE: 'square'
} as const;

export const AI_DISCLOSURE_TYPES = {
  GENERATED: 'generated',
  MODIFIED: 'modified'
} as const;

export type AiDisclosureType = (typeof AI_DISCLOSURE_TYPES)[keyof typeof AI_DISCLOSURE_TYPES];

/** Matches `ai:generated`, `ai:modified` and their `:position:<position>` variants; capture group 1 is the type. */
export const AI_DISCLOSURE_TAG_PATTERN = new RegExp(`^ai:(${Object.values(AI_DISCLOSURE_TYPES).join('|')})(?::|$)`);

export const AI_DISCLOSURE_POSITIONS = {
  TOP_LEFT: 'top-left',
  TOP_CENTER: 'top-center',
  TOP_RIGHT: 'top-right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_CENTER: 'bottom-center',
  BOTTOM_RIGHT: 'bottom-right'
} as const;

export type AiDisclosurePosition = (typeof AI_DISCLOSURE_POSITIONS)[keyof typeof AI_DISCLOSURE_POSITIONS];

export const AI_DISCLOSURE_DEFAULT_POSITION: AiDisclosurePosition = AI_DISCLOSURE_POSITIONS.BOTTOM_RIGHT;

/** Maps a media tag position to the imgix `mark-align` value. */
export const AI_DISCLOSURE_IMGIX_ALIGN: Record<AiDisclosurePosition, string> = {
  [AI_DISCLOSURE_POSITIONS.TOP_LEFT]: 'top,left',
  [AI_DISCLOSURE_POSITIONS.TOP_CENTER]: 'top,center',
  [AI_DISCLOSURE_POSITIONS.TOP_RIGHT]: 'top,right',
  [AI_DISCLOSURE_POSITIONS.BOTTOM_LEFT]: 'bottom,left',
  [AI_DISCLOSURE_POSITIONS.BOTTOM_CENTER]: 'bottom,center',
  [AI_DISCLOSURE_POSITIONS.BOTTOM_RIGHT]: 'bottom,right'
};

/** Width used to rasterize the source SVG before Imgix scales the overlay. */
export const AI_DISCLOSURE_WATERMARK_SOURCE_WIDTH = 480;

const AI_DISCLOSURE_SVG_BASE_URL = `${IMGIX_ASSETS_HOST}/images/svg`;

/** Imgix `mark-scale` (percent of the image width) per content category and device. */
export const AI_DISCLOSURE_WATERMARK_SCALE = {
  CARD: { mobile: 24, desktop: 24 },
  MAGAZINE: { mobile: 24, desktop: 12 },
  KEY_VISUAL: { mobile: 24, desktop: 12 },
  BLOG_KEY_VISUAL: { mobile: 30, desktop: 22 },
  EDITORIAL: { mobile: 16, desktop: 14 },
  DEFAULT: { mobile: 24, desktop: 24 }
} as const;

/** Imgix watermark assets containing the AI disclosure label text for KeyVisuals. */
export const AI_DISCLOSURE_KV_WATERMARKS: Record<AiDisclosureType, { url: string }> = {
  [AI_DISCLOSURE_TYPES.GENERATED]: {
    url: `${AI_DISCLOSURE_SVG_BASE_URL}/ai_generated_text.svg?fm=png&w=${AI_DISCLOSURE_WATERMARK_SOURCE_WIDTH}`
  },
  [AI_DISCLOSURE_TYPES.MODIFIED]: {
    url: `${AI_DISCLOSURE_SVG_BASE_URL}/ai_modified_text.svg?fm=png&w=${AI_DISCLOSURE_WATERMARK_SOURCE_WIDTH}`
  }
};

/** Imgix watermark assets for the AI disclosure badge. */
export const AI_DISCLOSURE_WATERMARKS: Record<AiDisclosureType, { url: string }> = {
  [AI_DISCLOSURE_TYPES.GENERATED]: {
    url: `${AI_DISCLOSURE_SVG_BASE_URL}/ai_generated.svg?fm=png&w=${AI_DISCLOSURE_WATERMARK_SOURCE_WIDTH}`
  },
  [AI_DISCLOSURE_TYPES.MODIFIED]: {
    url: `${AI_DISCLOSURE_SVG_BASE_URL}/ai_modified.svg?fm=png&w=${AI_DISCLOSURE_WATERMARK_SOURCE_WIDTH}`
  }
};

/** Distance of the watermark to the image edge in px */
export const AI_DISCLOSURE_WATERMARK_PADDING: Record<Device, number> = {
  desktop: 24,
  mobile: 12
};

export const AI_DISCLOSURE_BLOG_KV_WATERMARK_PADDING = 12;

export const AI_DISCLOSURE_GROUPED_MAGAZINE_ACCORDION_DESKTOP_WATERMARK_PADDING = 48;
